import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { LoggingService } from '@app/core/services/error-logging.service';
import { AccountsState } from '@app/core/store/states/accounts/accounts.state';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { RouterNavState } from '@app/core/store/states/router-nav/router-nav.state';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { format } from 'date-fns';
import add from 'date-fns/add';
import differenceInMonths from 'date-fns/differenceInMonths';
import endOfDay from 'date-fns/endOfDay';
import getMonth from 'date-fns/getMonth';
import getYear from 'date-fns/getYear';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import isSameMonth from 'date-fns/isSameMonth';
import parse from 'date-fns/parse';
import setMonth from 'date-fns/setMonth';
import setYear from 'date-fns/setYear';
import startOfDay from 'date-fns/startOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import sub from 'date-fns/sub';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Calendar, CompareTypes, DateRangeOption, DateRangesTypes, SideEnum } from '../../date-range.models';
import { DateRangeService } from '../../date-range.service';
import {
  AddRemoveMonth,
  CheckDatesAreSame,
  FormatDateRange,
  GetPreviousYearDateRange,
} from '../../date-range.utilities';

@Component({
  standalone: false,
  selector: 'adspt-date-range-selector',
  templateUrl: './date-range-selector.component.html',
  styleUrls: ['./date-range-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DateRangeService],
})
export class DateRangeSelectorComponent {
  readonly translations = this.translate.instant('global-filter.date-selector');

  readonly showNote$: Observable<boolean> = this.store.select(CustomerState.showAmz3DayDataSyncNote);

  readonly dates$: Observable<string> = this.store.select(RouterNavState.formattedDateQueryParam());

  isAfter = isAfter;
  selectedInput: 0 | 1 | 2 | 3 = 0;
  sideEnum = SideEnum;
  isCalendarOpen: boolean;
  locale = this.drs.locale;
  today = this.drs.today;
  tomorrow = this.drs.tomorrow;
  oneDayBeforeStartDate: Date | null;
  selectedCompareType = CompareTypes.PREVIOUS_PERIOD;
  compareTypes = [CompareTypes.PREVIOUS_PERIOD, CompareTypes.PREVIOUS_YEAR, CompareTypes.CUSTOM];
  toast = false;
  calendar = {
    left: <Calendar>{},
    right: <Calendar>{},
  };
  currentMonth = {
    left: null,
    right: null,
  };
  leftCurrentMonth: Date;
  rightCurrentMonth: Date;

  nowHoveredDate = null;

  inputClicked = false;
  pickingDate = false;
  pickingCompareDate = false;
  enableCompareSelection = false;

  @Input() dates: [Date, Date] = [this.today, endOfDay(new Date())];
  @Input() compareDates: [Date, Date] = [null, null];
  @Input() hasCompareRange = true;
  @Input() minDate: any = null;
  @Input() maxDate: any = this.today;

  @Output() rangeClicked = new EventEmitter();
  @Output() startDateChanged = new EventEmitter();
  @Output() endDateChanged = new EventEmitter();
  @Output() close = new EventEmitter();

  private _displayDate;
  private _displayCompareDate;
  private _old: any = { start: null, end: null, compareStart: null, compareEnd: null };

  private _setDayBeforeStartDate() {
    this.oneDayBeforeStartDate = !this.dates[0] ? null : sub(this.dates[0], { days: 1 });
  }

  get validForSubmit() {
    const validDates = !!this.dates[0] && !!this.dates[1];
    const validCompareDates = !!this.compareDates[0] && !!this.compareDates[1];
    if (this.hasCompareRange) {
      return validDates && validCompareDates;
    } else {
      return validDates;
    }
  }

  get isPrevPeriod() {
    return this.selectedCompareType === CompareTypes.PREVIOUS_PERIOD;
  }
  get isPrevYear() {
    return this.selectedCompareType === CompareTypes.PREVIOUS_YEAR;
  }
  get isLastMonthRangeActive() {
    return this.isPrevPeriod && this.ranges.find((d) => d.type === DateRangesTypes.LMN)?.active;
  }
  get activeType() {
    return this.isPrevPeriod && this.ranges.find((d) => d.active)?.type;
  }
  get displayCompareDate(): string {
    this._displayCompareDate = FormatDateRange(this.compareDates) ?? this._displayCompareDate;
    return this._displayCompareDate;
  }
  get displayDate(): string {
    this._displayDate = FormatDateRange(this.dates) ?? this._displayDate;
    return this._displayDate;
  }

  ranges: DateRangeOption[] = [];
  ranges$: Observable<DateRangeOption[]> = this.store.select(AccountsState.adpsertAccountStartDate).pipe(
    map((date) => {
      this.ranges = this.drs.ranges.slice(0);

      if (date) this.ranges.push({ ...this.drs.startDateRangeOption, value: [date, sub(this.today, { days: 1 })] });

      this.ranges.push({ ...this.drs.customRange });

      return this.ranges;
    }),
    tap(() => this._init()),
  );

  constructor(
    private store: Store,
    private translate: TranslateService,
    private loggingService: LoggingService,
    public drs: DateRangeService,
  ) {}

  private _init() {
    this._old.start = startOfDay(this.dates[0]);
    this._old.end = endOfDay(this.dates[1]);
    this._old.compareStart = startOfDay(this.compareDates[0]);
    this._old.compareEnd = endOfDay(this.compareDates[1]);

    this.setStartDate(this.dates[0]);
    this.setEndDate(this.dates[1]);

    this.updateMonthsInView();
    this.updateCalendars();

    if (this.hasCompareRange) {
      const compareType = CheckDatesAreSame(this.compareDates, GetPreviousYearDateRange(this.dates))
        ? CompareTypes.PREVIOUS_YEAR
        : CompareTypes.PREVIOUS_PERIOD;
      this.clickCompareRange(compareType, true);
    }

    this.setActiveRange();
  }

  private _setDate(date: any) {
    if (this.enableCompareSelection) {
      if (!this.pickingCompareDate) {
        // start date selected
        this.setStartDate(date);
        this.compareDates[1] = null;
        this.selectedInput = 3;
      } else {
        if (isBefore(date, this.compareDates[0]) === true) {
          const tmp = this.compareDates[0];
          this.setStartDate(date);
          this.setEndDate(tmp);
        } else {
          this.setEndDate(date);
        }
        this.selectedInput = 0;
      }
    } else {
      const [start, end] = this.dates;
      if (!start || end || isBefore(date, start)) {
        if (+date > +end) {
          this.dates[1] = null;
        }
        this.setStartDate(date);
        // remove compare selections when compare dates are invalid w.r.t to compare
        if (+this.compareDates[1] >= +this.dates[0] || +this.compareDates[0] >= +this.dates[0]) {
          this.compareDates[0] = null;
          this.compareDates[1] = null;
        }
        this.selectedInput = 1; // focus second input
        this.inputClicked = true;
      } else if (!end && isBefore(date, start)) {
        // special case: clicking the same date for start/end,
        // but the time of the end date is before the start date
        this.setEndDate(start);
        if (!this.hasCompareRange) {
          this.selectedInput = 0;
        } else {
          if (this.isPrevPeriod || this.isPrevYear) {
            this.selectedInput = 0;
          } else {
            this.selectedInput = 2;
            this.enableCompareSelection = true;
          }
          this.clickCompareRange(this.selectedCompareType);
        }
      } else {
        if (isBefore(date, start) === true) {
          this.setEndDate(start);
          this.setStartDate(date);
        } else {
          this.setEndDate(date);
        }
        if (!this.hasCompareRange) {
          // return focus to first one
          this.selectedInput = 0;
        } else {
          if (this.isPrevPeriod || this.isPrevYear) {
            this.selectedInput = 0;
            this.clickCompareRange(this.selectedCompareType);
          } else {
            if (this.selectedInput === 1 && !this.compareDates[0] && +this.compareDates[0] >= +this.dates[1]) {
              this.compareDates = [null, null];
            }
            this.selectedInput = 2;
            this.enableCompareSelection = true;
          }
        }
      }
    }
    this.updateView();
    this.setActiveRange();
  }

  private _reset() {
    this.calendar = <any>{ left: {}, right: {} };
    this.currentMonth = { left: null, right: null };
    this.leftCurrentMonth = null;
    this.rightCurrentMonth = null;
    this.selectedInput = 0;
    this.nowHoveredDate = null;
    this.inputClicked = false;
    this.pickingDate = false;
    this.pickingCompareDate = false;
    this.enableCompareSelection = false;
  }

  onStartDateChange(date) {
    this.dates[0] = date;

    //  new start date is after end date, replace the end date; basically range will be only one day
    if (+this.dates[1] < +date) {
      this.dates[1] = date;
    }

    if (+this.compareDates[0] >= +date) {
      this.compareDates = this.drs.getPreviousPeriodDates(this.dates);
      this.selectedCompareType = CompareTypes.PREVIOUS_PERIOD;
    } else if (+this.compareDates[1] >= +date) {
      const tmp = this.drs.getPreviousPeriodDates(this.dates, this.activeType) || [null, null];
      this.compareDates[1] = tmp[1];

      this.selectedCompareType = +tmp === +this.compareDates[0] ? CompareTypes.PREVIOUS_PERIOD : CompareTypes.CUSTOM;
    }

    this._setDayBeforeStartDate();

    this.updateView();
    this.setActiveRange();
  }
  onEndDateChange(date) {
    if (!this.dates[0]) {
      return;
    }

    this.dates[1] = date;

    this.updateView();
    this.setActiveRange();
  }
  onCompareStartDateChange(date) {
    const prevPeriodDates = this.drs.getPreviousPeriodDates(this.dates, this.activeType);
    this.selectedCompareType = +date === +prevPeriodDates[0] ? CompareTypes.PREVIOUS_PERIOD : CompareTypes.CUSTOM;

    this.compareDates[0] = date;
    //  new start date is after end date, replace the end date; basically range will be only one day
    if (+this.compareDates[1] < +date) {
      this.compareDates[1] = date;
    }

    this.pickingDate = false;
    this.pickingCompareDate = false;
    this.enableCompareSelection = true;
    this.updateView();
    this.setActiveRange();
  }
  onCompareEndDateChange(date) {
    if (!this.compareDates[0]) {
      return;
    }

    this.compareDates[1] = date;
    this.pickingDate = false;
    this.pickingCompareDate = false;
    this.enableCompareSelection = false;
    this.updateView();
    this.setActiveRange();
  }

  onStartDateClick() {
    this.selectedInput = 0;
    this.inputClicked = true;
    this.pickingDate = false;
    this.pickingCompareDate = false;
    this.enableCompareSelection = false;
    this.updateView();
  }
  onEndDateClick() {
    if (!this.dates[0]) return;

    this.selectedInput = 1;
    this.inputClicked = true;
    this.pickingDate = true;
    this.pickingCompareDate = false;
    this.enableCompareSelection = false;
    this.updateView();
  }
  onCompareStartDateClick() {
    this.selectedInput = 2;
    this.inputClicked = true;
    this.pickingDate = false;
    this.pickingCompareDate = false;
    this.enableCompareSelection = true;
    this.updateView();
  }
  onCompareEndDateClick() {
    this.selectedInput = 3;
    this.inputClicked = true;
    this.pickingDate = false;
    this.pickingCompareDate = true;
    this.enableCompareSelection = true;
    this.updateView();
  }

  onCustomRangeChange(val) {
    this.selectedCompareType = val;
    if (this.isPrevPeriod) {
      this.compareDates = this.drs.getPreviousPeriodDates(this.dates, this.activeType);
    } else if (this.isPrevYear) {
      this.compareDates = GetPreviousYearDateRange(this.dates);
    } else {
      this.selectedInput = 2;
    }
    this.clickCompareRange(val);
  }
  onDropdownChange(isVisible) {
    if (!isVisible) {
      this.onCancel();
      const customRange = this.ranges.find((d) => d.type === DateRangesTypes.CUSTOM && !d.active);
      if (customRange) {
        customRange.value = null;
        customRange.active = false;
      }
    }
  }

  onApply() {
    if (this.dates[0] && !this.dates[1]) {
      this.dates[1] = this.dates[0];
    }
    if (!this.validForSubmit) {
      return (this.toast = true);
    }
    this.isCalendarOpen = false;
    const payload: any = [[...this.dates]];
    this._old.start = this.dates[0];
    this._old.end = this.dates[1];
    if (this.hasCompareRange) {
      this._old.compareStart = this.compareDates[0];
      this._old.compareEnd = this.compareDates[1];
      payload.push([...this.compareDates]);
    }
    this.close.emit(payload);
    this._reset();
  }
  onCancel() {
    this.dates = [this._old.start, this._old.end];
    if (this.hasCompareRange) {
      this.compareDates = [this._old.compareStart, this._old.compareEnd];
    }
    this._reset();
    this.updateView();
  }

  hoverDate(side: SideEnum, row: number, col: number) {
    if (this.pickingDate || this.pickingCompareDate) {
      this.nowHoveredDate = this.drs.getHoveredDate(side, row, col);
      this.updateCalendars();
    }
  }

  /* #region  call when month or year changed */
  monthChanged(monthEvent: any, side: SideEnum) {
    const year = this.drs.calendarState[side].dropdowns.currentYear;
    const month = parseInt(monthEvent.target.value, 10);
    this.monthOrYearChanged(month, year, side);
  }

  yearChanged(yearEvent: any, side: SideEnum) {
    const month = this.drs.calendarState[side].dropdowns.currentMonth;
    const year = parseInt(yearEvent.target.value, 10);
    this.monthOrYearChanged(month, year, side);
  }

  monthOrYearChanged(month: number, year: number, side: SideEnum) {
    const isLeft = side === SideEnum.left;

    if (!isLeft) {
      if (year < getYear(this.dates[0]) || (year === getYear(this.dates[0]) && month < getMonth(this.dates[0]))) {
        month = getMonth(this.dates[0]);
        year = getYear(this.dates[0]);
      }
    }

    if (this.minDate) {
      if (year < getYear(this.minDate) || (year === getYear(this.minDate) && month < getMonth(this.minDate))) {
        month = getMonth(this.minDate);
        year = getYear(this.minDate);
      }
    }

    if (this.maxDate) {
      if (year > getYear(this.maxDate) || (year === getYear(this.maxDate) && month > getMonth(this.maxDate))) {
        month = getMonth(this.maxDate);
        year = getYear(this.maxDate);
      }
    }
    this.drs.calendarState[side].dropdowns.currentYear = year;
    this.drs.calendarState[side].dropdowns.currentMonth = month;
    if (isLeft) {
      this.currentMonth.left = setYear(setMonth(this.currentMonth.left, month), year);
      if (this.drs.options.linkedCalendars) {
        this.currentMonth.right = add(this.currentMonth.left, { months: 1 });
      }
    } else {
      this.currentMonth.right = setYear(setMonth(this.currentMonth.right, month), year);
      if (this.drs.options.linkedCalendars) {
        this.currentMonth.left = sub(this.currentMonth.right, { months: 1 });
      }
    }
    this.updateCalendars();
  }
  /* #endregion */

  clickPrev(side: SideEnum) {
    if (side === SideEnum.left) {
      this.currentMonth.left = AddRemoveMonth(this.currentMonth.left, -1);
      if (this.drs.options.linkedCalendars) {
        this.currentMonth.right = AddRemoveMonth(this.currentMonth.right, -1);
      }
    } else {
      this.currentMonth.right = AddRemoveMonth(this.currentMonth.right, -1);
    }
  }
  clickNext(side: SideEnum) {
    if (side === SideEnum.left) {
      this.currentMonth.left = AddRemoveMonth(this.currentMonth.left, 1);
    } else {
      this.currentMonth.right = AddRemoveMonth(this.currentMonth.right, 1);
      if (this.drs.options.linkedCalendars) {
        this.currentMonth.left = AddRemoveMonth(this.currentMonth.left, 1);
      }
    }
  }

  disableArrows(side: SideEnum, arrow: SideEnum) {
    switch (true) {
      case side === SideEnum.left && arrow === SideEnum.right:
        return differenceInMonths(this.currentMonth.right, this.currentMonth.left) < 2;
      case side === SideEnum.right && arrow === SideEnum.left:
        return differenceInMonths(this.currentMonth.right, this.currentMonth.left) < 2;
    }
  }

  clickDate(side: SideEnum, row: number, col: number) {
    const classes = this.drs.calendarState[side].classes[row][col].split(' ');
    if (!classes.includes('available') || classes.includes('off') || classes.includes('disable-selected-dates')) {
      return;
    }

    if (this.inputClicked) {
      this.inputClicked = false;
      switch (this.selectedInput) {
        case 0:
          this.dates[0] = null;
          if (+this.calendar[side].grid[row][col] > +this.dates[1]) {
            this.dates[1] = null;
          }
          break;
        case 1:
          this.dates[1] = null;
          break;
        case 2:
          this.compareDates = [null, null];
          break;
        case 3:
          this.compareDates[1] = null;
          break;
      }
    }

    this._setDate(this.calendar[side].grid[row][col]);
  }

  clickRange(label: DateRangesTypes) {
    this.enableCompareSelection = false;
    this.pickingDate = false;
    this.pickingCompareDate = false;

    this.ranges.forEach((d) => {
      d.active = label === d.type;
    });

    if (label === DateRangesTypes.CUSTOM) {
      this.selectedInput = 0;
      this.dates = [null, null];
      this._setDayBeforeStartDate();
      this.compareDates = [null, null];
      this.pickingCompareDate = false;
      this.pickingDate = false;
      this.enableCompareSelection = false;
      this.selectedCompareType = CompareTypes.CUSTOM;
      this.updateView();
      return;
    } else {
      if (this.selectedCompareType === CompareTypes.CUSTOM) this.selectedCompareType = CompareTypes.PREVIOUS_PERIOD;
    }

    let dates = this.ranges.find((d) => d.type === label)?.value;
    if (!dates) {
      dates = [this.dates[0] || this.today, this.dates[1] || this.today];
    }

    this.dates[0] = startOfDay(dates[0]);
    this._setDayBeforeStartDate();
    this.dates[1] = endOfDay(dates[1]);
    this.rangeClicked.emit({ label, dates });

    if (this.maxDate && isSameMonth(this.maxDate, dates[0])) {
      this.currentMonth.right = setMonth(this.currentMonth.right, getMonth(dates[0]));
      this.currentMonth.right = setYear(this.currentMonth.right, getYear(dates[0]));
      this.currentMonth.left = setMonth(this.currentMonth.left, getMonth(dates[0]) - 1);
      this.currentMonth.left = setYear(this.currentMonth.left, getYear(dates[1]));
    } else {
      this.currentMonth.left = setMonth(this.currentMonth.left, getMonth(dates[0]));
      this.currentMonth.left = setYear(this.currentMonth.left, getYear(dates[0]));
      // get the next year
      const nextMonth = add(dates[0], { months: 1 });
      this.currentMonth.right = setMonth(this.currentMonth.right, getMonth(nextMonth));
      this.currentMonth.right = setYear(this.currentMonth.right, getYear(nextMonth));
    }
    if (this.hasCompareRange) {
      this.clickCompareRange(this.selectedCompareType);
    }
    this.updateView();
    this.selectedInput = 0;
  }

  clickCompareRange(label = CompareTypes.PREVIOUS_PERIOD, isOnLoad = false) {
    if (this.hasCompareRange) {
      const isCustom = label === CompareTypes.CUSTOM;
      const isPrevYear = label === CompareTypes.PREVIOUS_YEAR;
      this.enableCompareSelection = isCustom;
      this.pickingCompareDate = false;
      if (!isOnLoad) {
        const prevDates = isPrevYear
          ? GetPreviousYearDateRange(this.dates)
          : this.drs.getPreviousPeriodDates(this.dates, !isCustom ? this.activeType : null);
        if (!!prevDates) {
          this.compareDates = prevDates;
        } else {
          return;
        }
      }
      this.updateView();
    } else {
      this.compareDates = [null, null];
    }
  }

  setStartDate(startDate) {
    let tmpStart;

    if (!startDate) return;

    if (typeof startDate === 'string') {
      tmpStart = parse(startDate, this.locale.format, this.today);
    }

    if (typeof startDate === 'object') {
      tmpStart = startDate;
    }

    tmpStart = startOfDay(tmpStart);

    if (this.minDate && isBefore(tmpStart, this.minDate)) {
      tmpStart = this.minDate;
    }

    if (this.maxDate && isAfter(tmpStart, this.maxDate)) {
      tmpStart = this.maxDate;
    }

    this.startDateChanged.emit({ startDate: tmpStart, isCompare: !!this.enableCompareSelection });

    if (this.enableCompareSelection) {
      this.pickingCompareDate = true;
      this.compareDates[0] = tmpStart;
    } else {
      this.pickingDate = true;
      this.dates[0] = tmpStart;
      this._setDayBeforeStartDate();
    }
    this.updateMonthsInView();
  }
  setEndDate(endDate) {
    let tmpEnd;
    let tmpStart;

    if (!endDate) return;

    if (this.enableCompareSelection) tmpStart = this.compareDates[0];
    else tmpStart = this.dates[0];

    if (typeof endDate === 'string') {
      tmpEnd = parse(endDate, this.locale.format, this.today);
    }
    if (typeof endDate === 'object') tmpEnd = endDate;

    tmpEnd = endOfDay(tmpEnd);
    if (isBefore(tmpEnd, tmpStart)) tmpEnd = tmpStart;

    if (this.maxDate && isAfter(tmpEnd, this.maxDate)) tmpEnd = this.maxDate;

    if (this.drs.options.dateLimit && isBefore(add(tmpStart, { days: this.drs.options.dateLimit }), tmpEnd))
      tmpEnd = add(tmpStart, { days: this.drs.options.dateLimit });

    this.endDateChanged.emit({ endDate: tmpEnd, isCompare: !!this.enableCompareSelection });

    if (this.enableCompareSelection) {
      this.pickingCompareDate = false;
      this.compareDates[1] = tmpEnd;
    } else {
      this.pickingDate = false;
      this.dates[1] = tmpEnd;
    }

    this.enableCompareSelection = false;
    this.updateMonthsInView();
  }

  setActiveRange() {
    if (!this.dates[0] || !this.dates[1]) return;

    let hasCompleted = false;
    let alreadyHasActiveRange = false;
    this.ranges.forEach((d) => {
      d.active = false;
      if (d.value) {
        if (
          !hasCompleted &&
          format(d.value[0], 'yyyyMMdd') + format(d.value[1], 'yyyyMMdd') ===
            format(this.dates[0], 'yyyyMMdd') + format(this.dates[1], 'yyyyMMdd')
        ) {
          d.active = true;
          alreadyHasActiveRange = true;
          hasCompleted = true;
        } else {
          d.active = false;
        }
      }
    });
    const customRange = this.ranges.find((d) => d.type === DateRangesTypes.CUSTOM && !d.active);
    if (customRange) {
      if (alreadyHasActiveRange) {
        customRange.value = null;
        customRange.active = false;
      } else {
        customRange.value = [...this.dates];
        customRange.active = true;
      }
    }
    if (this.hasCompareRange && this.compareDates.length) {
      try {
        const tmp = this.drs.getPreviousPeriodDates(this.dates, this.activeType) || [null, null];
        if (this.compareDates.filter((d) => !d).length === 2) {
          // if compare dates are null use prev period.
          this.selectedCompareType = CompareTypes.PREVIOUS_PERIOD;
        } else {
          const isPrevYear = CheckDatesAreSame(this.compareDates, GetPreviousYearDateRange(this.dates));
          this.selectedCompareType = isPrevYear
            ? CompareTypes.PREVIOUS_YEAR
            : this.compareDates.reduce((c, d) => c + +d, 0) === tmp.reduce((c, d) => c + +d, 0)
              ? CompareTypes.PREVIOUS_PERIOD
              : CompareTypes.CUSTOM;
        }
      } catch (error) {
        this.loggingService.logError('Date range set active range error', JSON.stringify(error), 'fatal');
      }
    }
  }
  disableRange(range: DateRangesTypes) {
    const rangeMarkers = this.ranges.find((d) => d.type === range)?.value;
    if (!rangeMarkers) return;

    const areBothBefore = rangeMarkers.every((date) => {
      if (!this.minDate) {
        return false;
      }
      return isBefore(date, this.minDate);
    });
    const areBothAfter = rangeMarkers.every((date) => {
      if (!this.maxDate) {
        return false;
      }
      return isAfter(date, this.maxDate);
    });

    return areBothBefore || areBothAfter;
  }

  updateMonthsInView() {
    const tmp = this.hasCompareRange && !!this.compareDates[0] ? this.compareDates[0] : this.dates[0];

    const formattedTmp = tmp && format(tmp, 'yyyy-MM');
    const formattedEndDate = this.dates[1] && format(this.dates[1], 'yyyy-MM');
    const leftMonthDate = this.currentMonth.left && format(this.currentMonth.left, 'yyyy-MM');
    const rightMonthDate = this.currentMonth.right && format(this.currentMonth.right, 'yyyy-MM');

    if (this.dates[1]) {
      // if both dates are visible already, do nothing
      if (
        this.currentMonth.left &&
        this.currentMonth.right &&
        ((tmp && this.calendar.left && formattedTmp === leftMonthDate) ||
          (tmp && this.calendar.right && formattedTmp === rightMonthDate)) &&
        (formattedEndDate === leftMonthDate || formattedEndDate === rightMonthDate)
      )
        return;

      if (tmp) {
        this.currentMonth.left = add(startOfMonth(tmp), { days: 1 }); // get second day of month
        if (
          !this.drs.options.linkedCalendars &&
          (getMonth(this.dates[1]) !== getMonth(tmp) || getYear(this.dates[1]) !== getYear(tmp))
        ) {
          this.currentMonth.right = add(startOfMonth(this.dates[1]), { days: 1 });
        } else {
          this.currentMonth.right = add(startOfMonth(tmp), { days: 1, months: 1 }); // get second day of next month
        }
      }
    } else if (!!tmp) {
      if (leftMonthDate !== formattedTmp && rightMonthDate !== formattedTmp) {
        this.currentMonth.left = add(startOfMonth(tmp), { days: 1 });
        this.currentMonth.right = add(startOfMonth(tmp), { days: 1, months: 1 });
      }
    }
    if (this.maxDate && this.drs.options.linkedCalendars && +this.currentMonth.right > this.maxDate) {
      this.currentMonth.right = add(startOfMonth(this.maxDate), { days: 1 });
      this.currentMonth.left = sub(add(startOfMonth(this.maxDate), { days: 1 }), { months: 1 });
    }
  }

  updateView() {
    this.updateMonthsInView();
    this.updateCalendars();
  }

  updateCalendars() {
    this.renderCalendar(SideEnum.left);
    this.renderCalendar(SideEnum.right);
  }

  renderCalendar(side: SideEnum) {
    this.calendar[side] = this.drs.getCalendar(side, this.dates, this.currentMonth[side], this.minDate, this.maxDate);

    this.drs.buildCells(
      side,
      this.calendar[side].grid,
      this.pickingDate,
      this.pickingCompareDate,
      this.hasCompareRange,
      this.enableCompareSelection,
      this.dates,
      this.compareDates,
      this.minDate,
      this.nowHoveredDate,
    );
  }

  getHeapData() {
    const value: any = {};
    this.ranges.forEach((item) => {
      if (item.active) value[item.heapName] = true;
    });

    if (this.hasCompareRange) {
      if (this.selectedCompareType === CompareTypes.PREVIOUS_PERIOD) {
        value.previousPeriod = true;
      } else if (this.selectedCompareType === CompareTypes.PREVIOUS_YEAR) {
        value.previousYear = true;
      } else {
        if (value.custom) {
          value.comparecustom = true;
        } else {
          value.custom = true;
        }
      }
    }
    return value;
  }
}
